import { Flex } from 'antd';
import classNames from 'classnames';
import { Duration } from 'dayjs/plugin/duration';

import layoutStyles from 'business/layout/single-card-page-with-filter-card/layout.module.scss';
import { ControlsContainer } from 'business/shift-report/report/pages/ReportView/components/controls-container';
import {
  ConstructionSiteFieldsFragment,
  GetIndicatorResultsQuery,
  GetPreviousPrintReportQuery,
  GetShiftReportPdfMetadataQuery,
} from 'generated/graphql';
import { SimpleCard } from 'ui/SimpleCard';

import { AvailabilityGraphContainer } from './components/availability-graph-container';
import { InProgressReport } from './components/in-progress-report';
import { ReportViewMobileMetadataContainer } from './components/metadata-container/mobile-container';
import { NoteContainer } from './components/notes-container';
import { ReportChartContainer } from './components/report-chart-container';
import { TasksFilesContainer } from './components/tasks-files-container/tasks-files-container';
import { TasksTableContainer } from './components/tasks-table-container';
import { tasksHasFiles } from './services';
import { ShowMoreOnMobileState, ShowMoreOnMobileStateKeys } from './types';

type ReportViewMobileProps = {
  report: NonNullable<GetShiftReportPdfMetadataQuery['shiftReport']>;
  showMore: ShowMoreOnMobileState;
  toggleShowMore: (key: ShowMoreOnMobileStateKeys) => () => void;

  previousReport: GetPreviousPrintReportQuery['shiftReport'][0] | undefined;
  averageExcavationPerRing: string | null;
  averageBuildPerRing: string | null;

  indicators: NonNullable<
    GetIndicatorResultsQuery['calculateIndicatorResults']
  >['data'];

  nbRingsBuild: number;
  activities: GetShiftReportPdfMetadataQuery['activities'];
  totalTasksTime: Duration;
  totalCriticalTasksTime: Duration;
  currentConstructionSite: ConstructionSiteFieldsFragment | null;
  hasManagerEditPermission: boolean;
  redirect: string;
  filters: string | null;
  previousReportId: string;
  nextReportId: string;
};

export const ReportViewMobile = ({
  report,
  showMore,
  toggleShowMore,
  previousReport,
  averageExcavationPerRing,
  averageBuildPerRing,
  indicators,
  nbRingsBuild,
  activities,
  totalTasksTime,
  totalCriticalTasksTime,
  currentConstructionSite,
  hasManagerEditPermission,
  redirect,
  filters,
  previousReportId,
  nextReportId,
}: ReportViewMobileProps) => {
  return (
    <div className={classNames(layoutStyles.page, 'page-appear')}>
      <Flex
        align="stretch"
        justify="center"
        className={layoutStyles.flexContainer}
        vertical
        gap={20}
      >
        <ControlsContainer
          redirect={redirect}
          filters={filters}
          report={report}
          currentConstructionSite={currentConstructionSite}
          hasManagerEditPermission={hasManagerEditPermission}
          previousReportId={previousReportId}
          nextReportId={nextReportId}
        />
        <InProgressReport operatorValidation={report.operatorValidation} />
        <SimpleCard>
          <ReportViewMobileMetadataContainer
            report={report}
            showMore={showMore}
            toggleShowMore={toggleShowMore}
            previousReport={previousReport}
            averageExcavationPerRing={averageExcavationPerRing}
            averageBuildPerRing={averageBuildPerRing}
            indicators={indicators}
            totalTasksTime={totalTasksTime}
            totalCriticalTasksTime={totalCriticalTasksTime}
          />
        </SimpleCard>

        <SimpleCard>
          <ReportChartContainer
            report={report}
            nbRingsBuild={nbRingsBuild}
            activities={activities}
          />
        </SimpleCard>

        {report.tasks.length ? (
          <SimpleCard>
            <TasksTableContainer
              showMore={showMore}
              toggleShowMore={toggleShowMore}
              activities={activities}
              tasks={report.tasks}
            />
          </SimpleCard>
        ) : null}

        <SimpleCard>
          <NoteContainer
            note={report.note}
            showMore={showMore}
            toggleShowMore={toggleShowMore}
          />
        </SimpleCard>

        <SimpleCard>
          <AvailabilityGraphContainer
            report={report}
            showMore={showMore}
            toggleShowMore={toggleShowMore}
          />
        </SimpleCard>

        {tasksHasFiles(report.tasks) ? (
          <SimpleCard>
            <TasksFilesContainer
              tasks={report.tasks}
              showMore={showMore}
              toggleShowMore={toggleShowMore}
            />
          </SimpleCard>
        ) : null}
      </Flex>
    </div>
  );
};
